/**
 * The corrective-evaluator seam — ADR-028 §3, ONE responsibility: given a
 * declared HingeProfile and a bend angle θ, return the corrective outputs
 * (helper-bone poses, morph weights). Pure, engine-free, no I/O. Everything
 * else is a caller: the bake pass samples clips through it, the viewport pose
 * driver evaluates it per pose change, and a future knee/glute consumer hands
 * it a different profile without touching the evaluator.
 *
 * θ EXTRACTION IS E0's LAW (hingeBendAngle): the driver's local delta from
 * bind is decomposed twist-first — pronation (twist about the bone's own long
 * axis) is stripped and REPORTED before the bend is read about the hinge axis,
 * so a turning wrist can never masquerade as flexion. What remains after both
 * extractions is the residual: the measured disagreement between the profile's
 * declared axes and the rotation actually played. Beyond tolerance it is the
 * caller's MARKER (never a silent identity, §7) — the evaluator reports, the
 * door decides.
 *
 * Units: θ in DEGREES at this seam (ramps are authored against Daz-style
 * 75°/135° stations; degrees keep the profile data legible). Leaf
 * translations are in NORMALIZED units — fractions of the profile's declared
 * reference length — because a family-shared ramp cannot carry millimetres
 * (§6: one character's swell baked into every girth was Codex's P1). The
 * caller multiplies by the character's measured segment length at the door.
 *
 * Depends on: math/quat, math/vec3, math/swing-twist. Depended on by: the
 * bake compiler pass and the viewport pose driver (both wiring, ADR-028 E1+).
 */
import { quatFromAxisAngle, quatMultiply, type Quat } from '../math/quat.js';
import { vec3Normalize, vec3Scale, type Vec3Data } from '../math/vec3.js';
import { localDeltaFromBind, rotationAngle, swingTwist } from '../math/swing-twist.js';

/** One station of a piecewise-linear θ→value ramp. θ strictly increasing across points. */
export interface ThetaRampPoint {
  readonly thetaDeg: number;
  readonly value: number;
}
export interface ThetaRamp {
  readonly points: readonly ThetaRampPoint[];
}

export interface HingeHelperHalfRotation {
  readonly kind: 'half-rotation';
  /** The helper bone's name in the augmented rig (wears the corrective name class, §6e). */
  readonly name: string;
  /** Fraction of θ the helper carries about the hinge axis — 0.5 for the classic E1 bone. */
  readonly factor: number;
}
export interface HingeHelperLeaf {
  readonly kind: 'leaf';
  readonly name: string;
  /** Displacement direction in the helper's parent (anchor) frame; normalized here, loudly. */
  readonly direction: Vec3Data;
  /** θ → displacement in NORMALIZED units (fraction of the reference segment length). */
  readonly ramp: ThetaRamp;
}
export type HingeHelperSpec = HingeHelperHalfRotation | HingeHelperLeaf;

export interface HingeMorphSpec {
  readonly name: string;
  /** θ → morph weight (typically 0..1; the Daz two-stage 75°/135° shape). */
  readonly ramp: ThetaRamp;
}

export interface HingeProfile {
  /** The driver bone whose local rotation carries the bend (e.g. R_Forearm). */
  readonly driver: string;
  /** Bend axis, declared in the driver's bind-local frame (family prior, jointFrames pattern). */
  readonly hingeAxisLocal: Vec3Data;
  /** The driver's long axis in the same frame — pronation is stripped about it before the bend is read. */
  readonly limbAxisLocal: Vec3Data;
  readonly helpers: readonly HingeHelperSpec[];
  readonly morphs: readonly HingeMorphSpec[];
}

export interface HingeBendReading {
  /** The 1-DOF bend, degrees — the only number correctives may drive on. */
  readonly thetaDeg: number;
  /** Twist about the limb axis (pronation/roll), degrees — the caller routes it to the twist ladder. */
  readonly pronationDeg: number;
  /** Rotation left after both extractions, degrees — the declared-vs-played axis disagreement (§7 marker fuel). */
  readonly residualDeg: number;
}

export interface HelperPose {
  readonly name: string;
  /** Present on half-rotation helpers: local rotation about the hinge axis. */
  readonly rotation?: Quat;
  /** Present on leaf helpers: displacement in NORMALIZED units along the declared direction. */
  readonly translation?: Vec3Data;
}

export interface HingeCorrectives {
  readonly thetaDeg: number;
  readonly helpers: readonly HelperPose[];
  readonly morphWeights: ReadonlyArray<{ readonly name: string; readonly weight: number }>;
}

/** Piecewise-linear ramp evaluation, clamped at both ends. Malformed ramps refuse loudly. */
export function evaluateRamp(ramp: ThetaRamp, thetaDeg: number): number {
  const pts = ramp.points;
  if (pts.length < 2) {
    throw new RangeError(`evaluateRamp: a ramp needs at least two points, got ${pts.length}`);
  }
  for (const pt of pts) {
    // NaN compares false with everything, so the ordering check below cannot
    // catch it — a NaN station or value would flow straight through to the
    // output despite the fail-loud contract (Codex P2, #102).
    if (!Number.isFinite(pt.thetaDeg) || !Number.isFinite(pt.value)) {
      throw new RangeError(`evaluateRamp: ramp points must be finite, got (θ=${pt.thetaDeg}, value=${pt.value})`);
    }
  }
  for (let i = 1; i < pts.length; i += 1) {
    if (pts[i]!.thetaDeg <= pts[i - 1]!.thetaDeg) {
      throw new RangeError(
        `evaluateRamp: θ stations must strictly increase (${pts[i - 1]!.thetaDeg}° then ${pts[i]!.thetaDeg}°)`,
      );
    }
  }
  if (!Number.isFinite(thetaDeg)) {
    throw new RangeError(`evaluateRamp: θ must be finite, got ${thetaDeg}`);
  }
  if (thetaDeg <= pts[0]!.thetaDeg) return pts[0]!.value;
  if (thetaDeg >= pts[pts.length - 1]!.thetaDeg) return pts[pts.length - 1]!.value;
  for (let i = 1; i < pts.length; i += 1) {
    const a = pts[i - 1]!;
    const b = pts[i]!;
    if (thetaDeg <= b.thetaDeg) {
      const t = (thetaDeg - a.thetaDeg) / (b.thetaDeg - a.thetaDeg);
      return a.value + t * (b.value - a.value);
    }
  }
  /* c8 ignore next 2 — unreachable: the clamp above bounds θ inside the stations */
  throw new Error('evaluateRamp: unreachable');
}

/**
 * The two axes must actually form a hinge frame: near-parallel hinge and limb
 * axes let a pure bend be consumed ENTIRELY as pronation by the first
 * extraction — θ reads 0, residual reads 0, and the corrective is silently
 * disabled with the marker fuel gone too (Codex P2, #102). Measured profiles
 * are perpendicular by construction (hinge = cross(limb, anterior)); this
 * guards the hand-written ones.
 */
function assertHingeFrame(profile: HingeProfile, caller: string): void {
  const h = vec3Normalize(profile.hingeAxisLocal);
  const l = vec3Normalize(profile.limbAxisLocal);
  const cx = h.y * l.z - h.z * l.y;
  const cy = h.z * l.x - h.x * l.z;
  const cz = h.x * l.y - h.y * l.x;
  const sep = Math.hypot(cx, cy, cz);
  if (sep < 0.2) {
    throw new RangeError(
      `${caller}: hinge and limb axes are near-parallel (|cross| = ${sep.toFixed(3)}) — ` +
        `a bend about one is pronation about the other, and the corrective would be silently disabled`,
    );
  }
}

/**
 * E0's split, in extraction order: (1) pronation = twist about the limb axis,
 * innermost (`delta = bend · pronation` under the q = swing·twist convention);
 * (2) bend = twist of the remainder about the hinge axis; (3) residual = the
 * rotation neither axis explains.
 */
export function hingeBendAngle(profile: HingeProfile, bindLocal: Quat, currentLocal: Quat): HingeBendReading {
  assertHingeFrame(profile, 'hingeBendAngle');
  const delta = localDeltaFromBind(bindLocal, currentLocal);
  const pron = swingTwist(delta, profile.limbAxisLocal);
  const bend = swingTwist(pron.swing, profile.hingeAxisLocal);
  return {
    thetaDeg: (bend.twistRadians * 180) / Math.PI,
    pronationDeg: (pron.twistRadians * 180) / Math.PI,
    residualDeg: (rotationAngle(bend.swing) * 180) / Math.PI,
  };
}

/** The seam: θ in, corrective outputs out. Profile shape errors refuse loudly (§7). */
export function evaluateHingeCorrectives(profile: HingeProfile, thetaDeg: number): HingeCorrectives {
  if (!Number.isFinite(thetaDeg)) {
    throw new RangeError(`evaluateHingeCorrectives: θ must be finite, got ${thetaDeg}`);
  }
  const seen = new Set<string>();
  for (const spec of [...profile.helpers, ...profile.morphs]) {
    if (seen.has(spec.name)) {
      throw new RangeError(`evaluateHingeCorrectives: duplicate corrective name "${spec.name}"`);
    }
    seen.add(spec.name);
  }
  const thetaRad = (thetaDeg * Math.PI) / 180;
  const helpers: HelperPose[] = profile.helpers.map((h) => {
    if (h.kind === 'half-rotation') {
      // The same share range driverShareOfBend enforces — a factor the driver
      // correction cannot pair with must not produce a pose here either.
      if (!Number.isFinite(h.factor) || h.factor < 0 || h.factor > 1) {
        throw new RangeError(`evaluateHingeCorrectives: "${h.name}" factor must be in [0,1], got ${h.factor}`);
      }
      return { name: h.name, rotation: quatFromAxisAngle(profile.hingeAxisLocal, h.factor * thetaRad) };
    }
    // The direction is applied AS DECLARED, never re-normalized (Codex P2,
    // #107): the graft normalizes in WORLD and then folds the leaf parent's
    // inverse scale in per-axis, so the stored vector is deliberately
    // non-unit — normalizing here would strip the scale compensation and a
    // scaled hierarchy would swell by its scale again. A zero direction
    // still names no displacement and refuses.
    const len = Math.hypot(h.direction.x, h.direction.y, h.direction.z);
    if (!Number.isFinite(len) || len < 1e-9) {
      throw new RangeError(`evaluateHingeCorrectives: leaf "${h.name}" has a zero/non-finite direction`);
    }
    return { name: h.name, translation: vec3Scale(h.direction, evaluateRamp(h.ramp, thetaDeg)) };
  });
  const morphWeights = profile.morphs.map((m) => ({ name: m.name, weight: evaluateRamp(m.ramp, thetaDeg) }));
  return { thetaDeg, helpers, morphWeights };
}

/**
 * The composed pose the DRIVER keeps once a half-rotation helper exists: the
 * helper takes `factor·θ`, so the driver's played local rotation must give
 * that share back or the arm bends factor·θ too far. Returns the driver's
 * corrected local rotation: bind · pronation · remaining-bend. The caller
 * applies it wherever the driver's track lands (bake rewrite or live driver).
 */
export function driverShareOfBend(
  profile: HingeProfile,
  bindLocal: Quat,
  currentLocal: Quat,
  helperFactor: number,
): Quat {
  if (!Number.isFinite(helperFactor) || helperFactor < 0 || helperFactor > 1) {
    throw new RangeError(`driverShareOfBend: helper factor must be in [0,1], got ${helperFactor}`);
  }
  assertHingeFrame(profile, 'driverShareOfBend');
  const delta = localDeltaFromBind(bindLocal, currentLocal);
  const pron = swingTwist(delta, profile.limbAxisLocal);
  const bend = swingTwist(pron.swing, profile.hingeAxisLocal);
  const keptBend = quatFromAxisAngle(profile.hingeAxisLocal, bend.twistRadians * (1 - helperFactor));
  // Rebuild in composition order: residual swing · kept bend · pronation, back under bind.
  const rebuilt = quatMultiply(bend.swing, quatMultiply(keptBend, pron.twist));
  return quatMultiply(bindLocal, rebuilt);
}
